import type { LegalContact, LegalDocumentContent } from '@/types/legal/legalTypes'

/**
 * Privacy policy and terms of use content layer.
 *
 * Plain-language drafts of both pages. Every statement is something the code does today;
 * `test/unit/content/legalCopy.spec.ts` pins the numbers and model names to the code that
 * sets them, so changing a rule fails there until the page is changed with it.
 */

// -----------------------------------------------------------------------------
// SEO & Page Meta
// -----------------------------------------------------------------------------

export const PRIVACY_SEO = {
  title: 'Privacy policy',
  description: 'What Avow collects when you join and document your property, who else sees it, and how long it is kept.',
}

export const TERMS_SEO = {
  title: 'Terms of use',
  description: 'Avow is a membership, not an insurer. The rules for deposits, payouts, the trial and the public adjuster review.',
}

// -----------------------------------------------------------------------------
// Shared
// -----------------------------------------------------------------------------

export const LEGAL_UPDATED_LABEL = 'Last updated'

/** Where members write about these pages or their data, at the foot of both pages. */
export const LEGAL_CONTACT: LegalContact = {
  heading: 'Contact us',
  body: 'For a question about these pages, or to see or delete what we hold about you, email us with the wallet address you sign in with.',
  email: 'support@itsavow.com',
}

/** The line and links at the foot of every page. The blog is its own site and opens in a new tab. */
export const LEGAL_FOOTER = {
  disclaimer: 'Avow is a membership, not an insurer. Nothing in Avow is legal, insurance or financial advice.',
  navLabel: 'Footer',
  links: [
    { label: 'Blog', to: 'https://blog.itsavow.com' },
    { label: 'Privacy policy', to: '/privacy' },
    { label: 'Terms of use', to: '/terms' },
  ],
}

// -----------------------------------------------------------------------------
// Privacy policy
// -----------------------------------------------------------------------------

export const PRIVACY_POLICY: LegalDocumentContent = {
  kicker: 'Privacy',
  title: 'Privacy policy',
  updated: 'September 25, 2026',
  lead: 'To join Avow you show us proof of insurance, and as a member you photograph what you own. This page says what we collect, why, who else sees it, and how long we keep it.',
  sections: [
    {
      id: 'collect',
      heading: 'What we collect',
      paragraphs: [],
      items: [
        {
          term: 'Your wallet address.',
          detail: 'You sign in with Coinbase Wallet, and your wallet address is your account. We don’t ask for your name or email to sign in.',
        },
        {
          term: 'Your proof of insurance.',
          detail: 'The document you upload, and the details read from it: carrier, policy number, deductible, start and end dates, type of insurance, the insured’s name, the property or mailing address, and the state.',
        },
        {
          term: 'Your check-in photos.',
          detail: 'The photos you take of your home, rental or car and what’s in it, and the details read from them, such as a VIN, mileage, brand, model and serial number, or a receipt’s store, date and amount.',
        },
        {
          term: 'Your claims.',
          detail: 'What you tell us about a loss: what happened, when, the type of loss, the address, the estimated damage and your insurer’s claim number. Also the documents you add to the claim.',
        },
        {
          term: 'Your membership.',
          detail: 'Your tier, its dates, your deposit as the vault reports it, and your payouts.',
        },
        {
          term: 'An email address, if you give one.',
          detail: 'When you apply, you can give one and choose what it’s for. If you start a trial and ask for reminders, we use it only to email you a week before the trial ends and once when it ends, and we delete it for that when the trial ends, when you move to a paid tier, or when you use the “Stop these emails” link in the email. If you tick the box for news from Avow, we keep it, with when you ticked it and the words you agreed to, to send you news about Avow, such as new features and tiers. Every one of those emails has a “Stop these emails” link, and using it deletes the address from our list. When you ask us to look again at a policy we couldn’t accept, we use the email you give to tell you the outcome.',
        },
      ],
      closing: [
        'We use your policy details only to confirm you hold an active policy your tier accepts, in a state Avow serves, and to set when your membership ends. We use your photos to check each one shows what was asked for, to work out your claim readiness score and payout boost, and to back up a claim when you share them.',
      ],
    },
    {
      id: 'ai',
      heading: 'How your uploads are read',
      paragraphs: [
        'AI models read your uploads so you get an answer in about a minute. Both go through OpenRouter, a service that passes each request to the model named here.',
      ],
      items: [
        {
          term: 'Proof of insurance',
          detail: 'is read by Google’s Gemini 3.5 Flash Lite, or by Gemini 2.5 Flash Lite when that one is unavailable. It pulls out the carrier, policy number, deductible, dates, type of insurance, insured’s name, address and state, and scores how likely the document is to be genuine.',
        },
        {
          term: 'Check-in photos',
          detail: 'are read by OpenAI’s GPT-4o mini. It checks the photo shows what the check-in asked for and pulls out details such as a serial number or VIN.',
        },
      ],
      closing: [
        'OpenRouter, Google and OpenAI receive the file itself, and their own privacy policies cover what they do with it. When a policy is rejected, its details are emailed to an Avow admin, and you can ask for a person to look again.',
      ],
    },
    {
      id: 'sharing',
      heading: 'Who else sees it',
      paragraphs: [],
      items: [
        {
          term: 'Avow admins.',
          detail: 'Admins review rejected policies and your requests to look again. Admins who run payouts can open any document a member has uploaded.',
        },
        {
          term: 'Your public adjuster.',
          detail: 'When you ask for a review of a claim, the adjuster it is sent to sees the claim’s description, type and date of loss, and the documents and photos you mark as shared. Everything you add to a claim is private until you share it.',
        },
        {
          term: 'The services we run on.',
          detail: 'Supabase holds our database and your files and runs sign-in. OpenRouter, Google and OpenAI read uploads, as above. Resend delivers our emails. Netlify hosts the site. Coinbase provides the wallet and the connection to Base, and when you buy or cash out USDC you do it with Coinbase, which receives your wallet address and IP address.',
        },
      ],
      closing: [
        'We don’t sell your information and we don’t share it for advertising.',
      ],
    },
    {
      id: 'onchain',
      heading: 'What’s public on Base',
      paragraphs: [
        'Deposits, withdrawals and payouts are transactions on Base, a public blockchain. Anyone can see your wallet address, what it deposited and what it was paid. No one can change or delete those records, including us.',
        'Your policy, photos and claims are never put on-chain. They stay in our database, linked to your wallet address.',
      ],
    },
    {
      id: 'retention',
      heading: 'How long we keep it',
      paragraphs: [
        'We keep your documentation, meaning your proof of insurance, your check-in photos and your claim documents, until you ask us to delete it. That includes after your membership ends: you keep access to it, so the proof is there if you need it for a claim later.',
        'To have it deleted, email us at the address at the end of this page, with the wallet address you sign in with. We’ll confirm the request is yours before deleting anything. Removing a document from a claim in the app takes it off the claim but keeps the file, so ask us if you want the file itself gone.',
        'You can also ask us to delete your whole account, once your membership has ended and you’ve claimed every payout owed to you. That deletes your sign-in, your membership, your claims and documentation, any email address you gave us, and our records of your payouts.',
        'A few things stay. We keep your policy number and insurer, apart from your account, so the same policy can’t get a second free trial. We keep the record of each payout round, because it backs what was paid on Base. Money in the vault stays under your wallet for you to withdraw, and nothing recorded on Base can be deleted by anyone.',
      ],
    },
    {
      id: 'browser',
      heading: 'Cookies and browser storage',
      paragraphs: [
        'The app keeps a few things in your browser: your sign-in session, your wallet connection, and whether you chose light or dark mode. We don’t use advertising or analytics cookies.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to this policy',
      paragraphs: [
        'If we change what we collect or who sees it, we’ll update this page and the date at the top.',
      ],
    },
  ],
}

// -----------------------------------------------------------------------------
// Terms of use
// -----------------------------------------------------------------------------

export const TERMS_OF_USE: LegalDocumentContent = {
  kicker: 'Terms',
  title: 'Terms of use',
  updated: 'September 23, 2026',
  lead: 'These terms cover using Avow and being a member. If anything here is unclear, ask us before you deposit.',
  sections: [
    {
      id: 'not-insurer',
      heading: 'Avow is not an insurer',
      paragraphs: [
        'Avow is a membership, not insurance. We are not an insurance company, agent or broker. We don’t sell insurance, we don’t pay claims, and membership doesn’t replace your policy. Only your insurer decides your claim.',
        'You need your own active renters, homeowners or auto policy to join, and it has to stay active for you to stay a member.',
      ],
    },
    {
      id: 'no-advice',
      heading: 'Not advice',
      paragraphs: [
        'Nothing Avow shows you is legal, insurance, tax or financial advice. That includes your claim readiness score and every payout figure. For advice about your situation, talk to a professional you choose.',
      ],
    },
    {
      id: 'joining',
      heading: 'Who can join',
      paragraphs: [
        'Each tier accepts certain policies. The trial and Bronze take renters, homeowners or auto. Silver takes renters or homeowners. Gold takes homeowners. Policies from Alaska, Alabama, South Dakota and Wyoming aren’t accepted.',
        'Your policy is checked when you upload it. A document that isn’t genuine proof of an active policy is rejected, and you can ask a person to look again.',
        'You sign in with your own wallet and you are responsible for it. Avow never holds your keys, can’t recover a lost wallet and can’t reverse a transaction.',
      ],
    },
    {
      id: 'deposit',
      heading: 'Your deposit',
      paragraphs: [
        'Your deposit is USDC in Avow’s vault on Base, which lends it on Aave to earn interest. You can withdraw it anytime, with no lockup or fee. To stay a member, keep your tier’s amount in: $100 for Bronze, $500 for Silver, $1,000 for Gold. The trial needs no deposit.',
        'Your deposit isn’t a bank account and isn’t insured by the FDIC or anyone else.',
      ],
    },
    {
      id: 'payouts',
      heading: 'Payouts',
      paragraphs: [
        'The vault keeps a share of the interest it earns as a fee, and those fees are what’s paid out. On the 1st and 15th of each month they’re split among eligible members by tier weight: 5 for the trial, 10 for Bronze, 51 for Silver and 102 for Gold. A member with a check-in photo accepted in that payout period counts at 1.2 times their weight.',
        'To be paid, you need to be past your first full payout cycle, hold an approved policy that hasn’t expired, be within your membership term, and keep your tier’s amount in the vault. Join between the 1st and 14th and your first payout is on the 1st of next month; join from the 15th and it’s the 15th of next month. Rejoining after a membership lapses starts that wait again.',
        'Payouts aren’t fixed or guaranteed. They move with Aave’s rate, with how much is in the vault and with how many members share each payout. Every payout figure in the app is an estimate.',
        'These rules are applied by Avow’s servers, not written into the smart contracts. Payouts are sent through the payout contract, and you collect them from your dashboard.',
      ],
    },
    {
      id: 'membership-end',
      heading: 'When a membership ends',
      paragraphs: [
        'We check memberships regularly. A membership is deactivated when its policy expires, when its deposit falls below the tier’s amount, or when a trial reaches its end. A deactivated membership isn’t paid.',
        'The one exception is after a claim: once a public adjuster approves your claim, you can go below your tier’s amount for three months and still be paid. Top it back up by then to stay a member.',
      ],
    },
    {
      id: 'trial',
      heading: 'The free trial',
      paragraphs: [
        'The trial lasts 90 days from the day it starts and needs no deposit. Up to 10 trial memberships can be active at once. When they’re full, you can join a paid tier instead.',
        'You can move up to a paid tier during the trial. A paid member can’t move back to the trial. When the trial ends, the membership is deactivated unless you’ve moved up.',
      ],
    },
    {
      id: 'adjuster',
      heading: 'The public adjuster review',
      paragraphs: [
        'A public adjuster is a claims professional who works for policyholders, not insurers. When you ask for a review of a claim, Avow sends it to an independent public adjuster, who looks at the claim and the documents you’ve shared and approves the claim or not.',
        'To ask for a review, your claim readiness score has to reach your tier’s bar: 90% for the trial and Bronze, 80% for Silver, and none for Gold.',
        'The review isn’t a claim with your insurer or a decision by your insurer, and it doesn’t promise any payment from them. The adjuster doesn’t work for Avow, and their views are their own. If you hire an adjuster to handle your claim, that agreement and its fees are between you and them. Public adjusters are licensed state by state, so check that one holds a license in your state before you hire them.',
      ],
    },
    {
      id: 'files',
      heading: 'Your files when you leave',
      paragraphs: [
        'When your membership ends, we keep your proof of insurance, check-in photos and claim documents, and you keep access to them, until you ask us to delete them. The privacy policy says how.',
        'Your deposit stays in your control, and you can withdraw it to your wallet anytime.',
      ],
    },
    {
      id: 'risk',
      heading: 'Risks of an on-chain product',
      paragraphs: [
        'Your deposit sits in smart contracts: Avow’s vault and Aave’s, on Base, in USDC. A bug or exploit in any of them, an outage or failure of Base or Aave, or USDC losing its dollar value could cost you some or all of your deposit. No one insures against that.',
        'The vault is an upgradeable contract. The accounts that control it can change its code and the share of interest it keeps as a fee.',
        'Transactions on Base are final. Check the details before you confirm one in your wallet.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to these terms',
      paragraphs: [
        'If we change these terms, we’ll update this page and the date at the top.',
      ],
    },
  ],
}
